<template>
  <div class="device-reading-dataset app-page app-page--fill">
    <div class="dataset-panel">
      <div class="toolbar">
        <el-select
          v-model="statusFilter"
          placeholder="AI读数状态"
          clearable
          class="status-filter"
          @change="handleStatusChange"
          @clear="handleStatusChange"
        >
          <el-option label="正常" value="正常" />
          <el-option label="异常" value="异常" />
        </el-select>
        <div class="toolbar-actions">
          <el-button
            v-if="can('查询')"
            type="primary"
            circle
            size="small"
            class="app-refresh-btn"
            :icon="Refresh"
            :loading="loading"
            @click="loadData"
            title="刷新"
          />
        </div>
      </div>

      <el-table
        :data="tableData"
        border
        stripe
        v-loading="loading"
        class="dataset-table"
        height="100%"
      >
        <el-table-column type="index" label="序号" width="70" align="center" />
        <el-table-column label="现场照片" width="150" align="center">
          <template #default="{ row }">
            <div class="photo-cell">
              <div v-if="getPhotoState(row).loading" class="photo-loading">
                <el-icon class="is-loading"><Loading /></el-icon>
              </div>
              <el-image
                v-else-if="getPhotoState(row).url"
                class="photo-thumb"
                :src="getPhotoState(row).url"
                :preview-src-list="can('照片查看') ? [getPhotoState(row).url] : []"
                fit="cover"
                preview-teleported
              />
              <div v-else class="photo-empty">无图</div>
              <div class="file-name" :title="row.文件名 || ''">{{ row.文件名 || '-' }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="上传时间" label="上传时间" min-width="170" show-overflow-tooltip>
          <template #default="{ row }">
            {{ formatTime(row.上传时间) }}
          </template>
        </el-table-column>
        <el-table-column prop="AI读数状态" label="AI读数状态" width="110" align="center">
          <template #default="{ row }">
            <el-tag :type="getStatusTagType(row.AI读数状态)">
              {{ row.AI读数状态 || '-' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="AI读数" min-width="220">
          <template #default="{ row }">
            <div class="reading-list">
              <div
                v-for="item in normalizeReadings(row.AI读数, row.AI读数json)"
                :key="`${item.title}-${item.value}`"
                class="reading-item"
              >
                <span class="reading-title">{{ item.title || '-' }}</span>
                <span class="reading-value">{{ formatReadingValue(item.value) }}</span>
              </div>
              <span v-if="normalizeReadings(row.AI读数, row.AI读数json).length === 0" class="empty-text">-</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="实际读数" min-width="220">
          <template #default="{ row }">
            <div class="reading-list">
              <div
                v-for="item in normalizeReadings(row.实际读数, row.实际读数json)"
                :key="`${item.title}-${item.value}`"
                class="reading-item"
              >
                <span class="reading-title">{{ item.title || '-' }}</span>
                <span class="reading-value">{{ formatReadingValue(item.value) }}</span>
              </div>
              <span v-if="normalizeReadings(row.实际读数, row.实际读数json).length === 0" class="empty-text">-</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="读数对比" min-width="260">
          <template #default="{ row }">
            <div class="compare-list">
              <div
                v-for="item in buildReadingCompare(row)"
                :key="item.title"
                class="compare-item"
                :class="{ 'compare-item--mismatch': item.mismatched }"
              >
                <span class="compare-title">{{ item.title }}</span>
                <span class="compare-values">
                  <span>AI: {{ item.ai }}</span>
                  <span>实际: {{ item.actual }}</span>
                </span>
              </div>
              <span v-if="buildReadingCompare(row).length === 0" class="empty-text">-</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="上传人ID" label="上传人ID" min-width="150" show-overflow-tooltip>
          <template #default="{ row }">
            {{ row.上传人ID || '-' }}
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-bar">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="pagination.total"
          layout="total, sizes, prev, pager, next, jumper"
          small
          @size-change="handlePageSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { reactive, ref, onMounted, onBeforeUnmount } from 'vue'
import { ElMessage } from 'element-plus'
import { Loading, Refresh } from '@element-plus/icons-vue'
import DeviceReadingDatasetApi, {
  type DeviceReadingDatasetItem,
  type ReadingValue,
} from '@/network/apis/DeviceReadingDataset'
import { list } from '@/network'
import { useRouteTitle } from '@/utils/useRouteTitle'
import { useButtonPermission } from '@/utils/buttonPermission'

interface PhotoState {
  url: string
  loading: boolean
  error: boolean
}

useRouteTitle('设备读数数据集')
const { can } = useButtonPermission('/visualModel/DeviceReadingDataset/index.vue')

const loading = ref(false)
const tableData = ref<DeviceReadingDatasetItem[]>([])
const statusFilter = ref('')
const photoMap = ref<Record<string, PhotoState>>({})

const pagination = reactive({
  page: 1,
  pageSize: 20,
  total: 0,
})

const getResponseData = (res: any) => res?.response || res

const buildStatusFilters = () => {
  if (!statusFilter.value) return []
  return [
    {
      FieldName: 'AI读数状态',
      FiedType: 'string',
      Type: 3,
      Value1: statusFilter.value,
      Value2: '',
      Ifalso: true,
    },
  ]
}

const formatTime = (value?: string) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const pad = (num: number) => String(num).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

const safeParseJson = (value?: string) => {
  if (!value || typeof value !== 'string') return null
  try {
    return JSON.parse(value)
  } catch (error) {
    return null
  }
}

const normalizeReadings = (readings?: ReadingValue[] | string, jsonValue?: string): ReadingValue[] => {
  const source = Array.isArray(readings)
    ? readings
    : safeParseJson(typeof readings === 'string' ? readings : jsonValue)

  if (!Array.isArray(source)) return []
  return source.map((item: any) => ({
    title: String(item?.title ?? item?.Title ?? item?.名称 ?? ''),
    value: item?.value ?? item?.Value ?? item?.值 ?? '',
  }))
}

const formatReadingValue = (value: unknown) => {
  if (value === undefined || value === null || value === '') return '-'
  return String(value)
}

const isSameReadingValue = (aiValue: unknown, actualValue: unknown) => {
  return formatReadingValue(aiValue).trim() === formatReadingValue(actualValue).trim()
}

const buildReadingCompare = (row: DeviceReadingDatasetItem) => {
  const aiReadings = normalizeReadings(row.AI读数, row.AI读数json)
  const actualReadings = normalizeReadings(row.实际读数, row.实际读数json)
  const titleSet = new Set<string>()
  aiReadings.forEach(item => titleSet.add(item.title || '-'))
  actualReadings.forEach(item => titleSet.add(item.title || '-'))

  return Array.from(titleSet).map((title) => {
    const ai = aiReadings.find(item => (item.title || '-') === title)
    const actual = actualReadings.find(item => (item.title || '-') === title)
    return {
      title,
      ai: formatReadingValue(ai?.value),
      actual: formatReadingValue(actual?.value),
      mismatched: !isSameReadingValue(ai?.value, actual?.value),
    }
  })
}

const getStatusTagType = (status?: string) => {
  if (status === '正常') return 'success'
  if (status === '异常') return 'danger'
  return 'info'
}

const getPhotoState = (row: DeviceReadingDatasetItem): PhotoState => {
  const fileName = row.文件名 || ''
  return photoMap.value[fileName] || { url: '', loading: false, error: false }
}

const clearPhotos = () => {
  Object.values(photoMap.value).forEach((item) => {
    if (item.url?.startsWith('blob:')) {
      URL.revokeObjectURL(item.url)
    }
  })
  photoMap.value = {}
}

const updatePhotoState = (fileName: string, patch: Partial<PhotoState>) => {
  const current = photoMap.value[fileName] || { url: '', loading: false, error: false }
  if (current.url?.startsWith('blob:') && patch.url && patch.url !== current.url) {
    URL.revokeObjectURL(current.url)
  }
  photoMap.value = {
    ...photoMap.value,
    [fileName]: {
      ...current,
      ...patch,
    },
  }
}

const loadPhotos = (rows: DeviceReadingDatasetItem[]) => {
  const fileNames = Array.from(new Set(rows.map(row => row.文件名).filter(Boolean))) as string[]
  fileNames.forEach((fileName) => {
    const cachedPhoto = photoMap.value[fileName]
    if (cachedPhoto?.url || cachedPhoto?.loading) {
      return
    }

    updatePhotoState(fileName, { url: '', loading: true, error: false })
    list.downloadPhoto({ 文件名: fileName })
      .then((blob) => {
        const url = URL.createObjectURL(blob)
        updatePhotoState(fileName, { url, loading: false, error: false })
      })
      .catch((error) => {
        console.error(`加载现场照片失败: ${fileName}`, error)
        updatePhotoState(fileName, { url: '', loading: false, error: true })
      })
  })
}

const loadData = async () => {
  loading.value = true
  try {
    const res: any = await DeviceReadingDatasetApi.list(
      {
        page: pagination.page,
        key: '',
        intPageSize: pagination.pageSize,
      },
      buildStatusFilters()
    )
    const response = getResponseData(res)
    tableData.value = Array.isArray(response?.data) ? response.data : []
    pagination.total = Number(response?.dataCount || tableData.value.length || 0)
    loadPhotos(tableData.value)
  } catch (error) {
    console.error('加载设备读数数据集列表失败:', error)
    tableData.value = []
    pagination.total = 0
    ElMessage.error('加载设备读数数据集列表失败')
  } finally {
    loading.value = false
  }
}

const handleStatusChange = () => {
  pagination.page = 1
  loadData()
}

const handlePageChange = (page: number) => {
  pagination.page = page
  loadData()
}

const handlePageSizeChange = (pageSize: number) => {
  pagination.pageSize = pageSize
  pagination.page = 1
  loadData()
}

onMounted(() => {
  loadData()
})

onBeforeUnmount(() => {
  clearPhotos()
})
</script>

<style scoped lang="less">
.device-reading-dataset {
  .dataset-panel {
    background: #fff;
    border-radius: 8px;
    padding: 16px;
    min-height: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  .status-filter {
    width: 220px;
  }

  .toolbar-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .dataset-table {
    flex: 1;
    min-height: 0;
  }

  .photo-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
  }

  .photo-thumb,
  .photo-loading,
  .photo-empty {
    width: 96px;
    height: 64px;
    border-radius: 6px;
    border: 1px solid #dcdfe6;
    background: #f5f7fa;
  }

  .photo-loading,
  .photo-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
    font-size: 13px;
  }

  .file-name {
    width: 120px;
    color: #606266;
    font-size: 12px;
    line-height: 18px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .reading-list,
  .compare-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .reading-item,
  .compare-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 26px;
    padding: 4px 8px;
    border-radius: 6px;
    background: #f7f9fc;
  }

  .compare-item--mismatch {
    border: 1px solid #f56c6c;
    background: #fef0f0;
  }

  .reading-title,
  .compare-title {
    color: #606266;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .compare-item--mismatch .compare-title,
  .compare-item--mismatch .compare-values {
    color: #d03050;
  }

  .reading-value {
    color: #303133;
    font-weight: 600;
  }

  .compare-values {
    display: flex;
    gap: 10px;
    color: #303133;
    font-weight: 600;
    white-space: nowrap;
  }

  .empty-text {
    color: #909399;
  }

  .pagination-bar {
    display: flex;
    justify-content: flex-end;
  }
}
</style>
